import { useState } from "react";
import { Link } from "react-router-dom";
import { X, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface LegalDocument {
  id: string;
  type: string;
  version: string;
  content: string;
  published_at: string;
  is_active: boolean;
}

interface PendingDocuments {
  privacyPolicy: LegalDocument | null;
  terms: LegalDocument | null;
}

interface LegalUpdateBannerProps {
  pendingDocuments: PendingDocuments;
  onDismiss: () => void;
}

const LegalUpdateBanner = ({ pendingDocuments, onDismiss }: LegalUpdateBannerProps) => {
  const { user } = useAuth();
  const [isAccepting, setIsAccepting] = useState(false);

  const needsPrivacy = !!pendingDocuments.privacyPolicy;
  const needsTerms = !!pendingDocuments.terms;

  const handleAcceptAndDismiss = async () => {
    if (!user) {
      onDismiss();
      return;
    }

    setIsAccepting(true);
    try {
      const updates: Record<string, string> = {};

      if (needsPrivacy && pendingDocuments.privacyPolicy) {
        updates.accepted_privacy_policy_version = pendingDocuments.privacyPolicy.version;
        updates.accepted_privacy_policy_at = new Date().toISOString();
      }

      if (needsTerms && pendingDocuments.terms) {
        updates.accepted_terms_version = pendingDocuments.terms.version;
        updates.accepted_terms_at = new Date().toISOString();
      }

      await supabase
        .from("fan_profiles")
        .update(updates)
        .eq("user_id", user.id);

      onDismiss();
    } catch (err) {
      console.error("Error accepting legal documents:", err);
      onDismiss();
    } finally {
      setIsAccepting(false);
    }
  };

  const getDocumentLinks = () => {
    const links = [];
    if (needsPrivacy) {
      links.push(
        <Link
          key="privacy"
          to="/privacy"
          className="underline hover:text-foreground transition-colors"
        >
          Privacy Policy
        </Link>
      );
    }
    if (needsTerms) {
      links.push(
        <Link
          key="terms"
          to="/terms"
          className="underline hover:text-foreground transition-colors"
        >
          Terms & Conditions
        </Link>
      );
    }
    return links;
  };

  const documentLinks = getDocumentLinks();

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-primary/10 border-b border-primary/20 backdrop-blur-sm">
      <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 text-sm text-foreground">
          <FileText className="h-4 w-4 text-primary shrink-0" />
          <span>
            We've updated our{" "}
            {documentLinks.map((link, i) => (
              <span key={i}>
                {i > 0 && " and "}
                {link}
              </span>
            ))}
            . By continuing to use Bortal, you agree to these updates.
          </span>
        </div>
        
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            variant="ghost"
            onClick={handleAcceptAndDismiss}
            disabled={isAccepting}
            className="text-xs"
          >
            {isAccepting ? "..." : "Got it"}
          </Button>
          <button
            onClick={handleAcceptAndDismiss}
            className="p-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LegalUpdateBanner;
